/* =============================================================================
   Crumpaw · gallery image warming
   -----------------------------------------------------------------------------
   Colour swaps are slow for one reason on both galleries: the picture is not in
   the browser cache yet when the shopper asks for it.

     · The harness has a single stage <img> whose src is rewritten on swap, so
       every colour is a cold fetch.
     · The bowls render one <img> per colour, but every one bar the first is
       `hidden loading="lazy"` — which the browser never fetches until it is
       shown, making the first view of each colour a cold fetch too.

   So this warms them: on hover or touch of a swatch, and then quietly across
   the rest once the page has gone idle.

   It always warms through `srcset` and `sizes` rather than a bare URL, so the
   browser picks the same candidate the stage will pick — the displayed size,
   never the full-resolution zoom copy.

   Shared by every product template. The heroes only tell it which swatch maps
   to which picture; the fetching, the batching and the opt-outs live here.
   ========================================================================== */

(() => {
  'use strict';

  /* Keyed by srcset (or src) rather than element, because two swatches can
     legitimately point at the same photograph. */
  const warmed = new Set();

  /** Metered or very slow connections should not be spent on speculation. */
  const stingy = () => {
    const c = navigator.connection;
    if (!c) return false;
    return !!c.saveData || /(^|-)2g$/.test(c.effectiveType || '');
  };

  const idle = (fn) =>
    window.requestIdleCallback
      ? window.requestIdleCallback(fn, { timeout: 4000 })
      : window.setTimeout(fn, 1500);

  /**
   * Pull the picture out of the browser at the size it will be shown at.
   *
   * @param {string} src Fallback URL for a browser with no srcset support.
   * @param {string} [srcset] The candidate list the stage will choose from.
   * @param {string} [sizes] The stage's own sizes attribute.
   */
  const warm = (src, srcset, sizes) => {
    const key = srcset || src;
    if (!key || warmed.has(key)) return;
    warmed.add(key);

    const img = new Image();
    img.decoding = 'async';
    // sizes must be set before srcset, or the first candidate choice is made
    // against the default 100vw and fetches a larger file than needed.
    if (sizes) img.sizes = sizes;
    if (srcset) img.srcset = srcset;
    if (src) img.src = src;
  };

  /** The stage the harness gallery swaps into, for its sizes attribute. */
  const stageSizes = () => {
    const stage = document.querySelector('[data-cph="gallery-main"]');
    return stage ? stage.getAttribute('sizes') : null;
  };

  /**
   * What picture does this element stand for?
   *
   * @param {Element} el A thumbnail, a hidden colour base, or a tagged swatch.
   * @returns {{src: string, srcset: string, sizes: string}|null}
   */
  const sourceOf = (el) => {
    if (!el || !el.dataset) return null;

    if (el.tagName === 'IMG') {
      return {
        src: el.getAttribute('src'),
        srcset: el.getAttribute('srcset'),
        sizes: el.getAttribute('sizes'),
      };
    }

    // Harness gallery thumbnails already carry the stage's own candidates.
    if (el.dataset.cphFull) {
      return {
        src: el.dataset.cphFull,
        srcset: el.dataset.cphSrcset,
        sizes: stageSizes(),
      };
    }

    if (el.dataset.cpWarmSrc) {
      return {
        src: el.dataset.cpWarmSrc,
        srcset: el.dataset.cpWarmSrcset,
        sizes: el.dataset.cpWarmSizes,
      };
    }

    return null;
  };

  const warmElement = (el) => {
    const source = sourceOf(el);
    if (source) warm(source.src, source.srcset, source.sizes);
  };

  /**
   * Warm whatever an element points at. A `data-cp-warm` attribute holds a CSS
   * selector for the picture; anything else stands for itself.
   *
   * @param {Element} el
   */
  const warmFor = (el) => {
    if (!el) return;
    const selector = el.dataset && el.dataset.cpWarm;
    if (!selector) {
      warmElement(el);
      return;
    }
    try {
      document.querySelectorAll(selector).forEach(warmElement);
    } catch (error) {
      /* A hand-edited selector should never take the page down. */
    }
  };

  /* ------------------------------------------------------------- hovering */

  const TARGETS = '[data-cp-warm], [data-cph="thumb"]';

  const onPoint = (event) => {
    const node = event.target;
    if (!node || !node.closest) return;
    const host = node.closest(TARGETS);
    if (host) warmFor(host);
  };

  document.addEventListener('pointerover', onPoint, { passive: true });
  document.addEventListener('touchstart', onPoint, { passive: true });
  document.addEventListener('focusin', onPoint);

  /* ---------------------------------------------------------------- idle */

  /* Every picture a gallery can swap to. Deliberately not [data-cpsb-full],
     which is the zoom copy and has no business being fetched speculatively. */
  const SWEEP = '[data-cph="thumb"], [data-cpsb="preview-base"], [data-cp-warm-src]';
  const BATCH = 4;

  const sweep = () => {
    if (stingy()) return;
    const list = Array.from(document.querySelectorAll(SWEEP));
    let at = 0;
    const step = () => {
      for (let n = 0; n < BATCH && at < list.length; n += 1, at += 1) {
        warmElement(list[at]);
      }
      if (at < list.length) idle(step);
    };
    idle(step);
  };

  if (document.readyState === 'complete') {
    idle(sweep);
  } else {
    window.addEventListener('load', () => idle(sweep), { once: true });
  }

  /* The heroes own the swatch-to-picture mapping, so hand them a way in. */
  window.cpWarm = { warm, warmFor, element: warmElement };
})();
